import { useDocumentsTogether } from "@shared/components/providers/SupergrainProvider"
import type { CardColorType } from "@shared/models/card-stack"
import { CARD_COLORS } from "@shared/models/card-stack"
import type { Comment } from "@shared/models/comment"
import { objectId } from "@shared/util/object-id"
import type { ExtensionCardGoogleClassroomAttrs } from "@tiptap-content/extensions/card-google-classroom/card-google-classroom-content-extension"
import type { ExtensionCardTitleAttrs } from "@tiptap-content/extensions/card-title/card-title-content-extension"
import type { CardExtensionAttrs } from "@tiptap-content/extensions/cards/card-content-extensions"
import type { Editor } from "@tiptap/core"
import { Node as PMNode, type Schema } from "@tiptap/pm/model"
import { useEditorState } from "@tiptap/react"
import { asCardId, type CardId } from "../analytics/types"

// ----------
// Card properties
// ----------

export interface CardAttrsBase {
  id: string
  parentCardId: string | null
  commentIds: string[]
  isPublic: boolean
}

/**
 * Base attrs for a brand-new card, i.e., one that isn't a copy of an existing card.
 * Includes a fresh id.
 */
export function newCardAttrs(): CardAttrsBase {
  return {
    id: objectId(),
    parentCardId: null,
    commentIds: [],
    isPublic: false,
  }
}

/**
 * Given a card node (in group "card"), returns its id. Else returns null.
 */
export function getCardId(cardNode: PMNode | null | undefined): CardId | null {
  if (!cardNode || !cardNode.type.isInGroup("card")) return null
  const raw: unknown = cardNode.attrs.id
  return typeof raw === "string" && raw.length > 0 ? asCardId(raw) : null
}

/**
 * Given a card node (in group "card"), returns its title. Else returns null.
 */
export function getCardTitle(cardNode: PMNode): string | null {
  switch (cardNode.type.name) {
    case "titledCard":
      const cardTitle = cardNode.firstChild!
      return cardTitle.textContent
    case "untitledCard":
      return "Untitled"
    case "cardGoogleClassroom":
      return (cardNode.attrs as ExtensionCardGoogleClassroomAttrs).title
    default:
      return null
  }
}

export function replaceCardPreservingIdentity(destinationCard: PMNode, sourceCard: PMNode): PMNode {
  const destinationAttrs = destinationCard.attrs as CardAttrsBase
  return sourceCard.type.create(
    {
      ...(sourceCard.attrs as CardAttrsBase),
      id: destinationAttrs.id,
      parentCardId: destinationAttrs.parentCardId,
      commentIds: destinationAttrs.commentIds,
    } satisfies CardAttrsBase,
    sourceCard.content,
    sourceCard.marks,
  )
}

/**
 * Returns the id of the card enclosing `pos` in `doc`, or null when the position sits outside every card
 * (the lesson title, the gaps between cards, or an editor without cards).
 * `pos` is clamped into the document, so a position
 * carried over from another document can never throw here.
 */
export function getContainingCardId(doc: PMNode, pos: number): CardId | null {
  const clamped = Math.max(0, Math.min(pos, doc.content.size))
  const $pos = doc.resolve(clamped)
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const id = getCardId($pos.node(depth))
    if (id) return id
  }
  return null
}

/**
 * The color to give the next card added to `doc`, cycling through the card colors
 * (minus black, which we don't generate).
 */
export function nextCardColor(doc: PMNode): CardColorType {
  const colorfulCardColors = CARD_COLORS.filter((color) => color !== "black")
  const cardsLength = Math.max(doc.childCount - 1, 0)
  return colorfulCardColors[cardsLength % colorfulCardColors.length]
}

/**
 * Returns whether the given card is the last one in the editor.
 *
 * Use a @tiptap/react selector so that this updates when another card is appended
 * (which doesn't affect another card's node, hence doesn't re-render its node view).
 */
export function useIsLastCard(editor: Editor, cardId: string): boolean {
  return useEditorState({
    editor,
    selector: ({ editor }) => getCardId(editor.state.doc.lastChild) === cardId,
  })
}

// ----------
// Creating cards
// ----------

/** An empty titledCard: an empty cardTitle of the given color, plus an empty paragraph. */
export function createTitledCard(schema: Schema, color: CardColorType, bodyText?: string): PMNode {
  return schema.node("titledCard", newCardAttrs() satisfies CardExtensionAttrs, [
    schema.node("cardTitle", { color } satisfies ExtensionCardTitleAttrs),
    schema.node("paragraph", null, bodyText ? schema.text(bodyText) : undefined),
  ])
}

/** An empty untitledCard: just an empty paragraph. */
export function createUntitledCard(schema: Schema): PMNode {
  return schema.node("untitledCard", newCardAttrs() satisfies CardExtensionAttrs, [
    schema.node("paragraph"),
  ])
}

// ----------
// Card comment helpers
// ----------

export function useCardComments(attrs: CardAttrsBase): Comment[] {
  const comments = useDocumentsTogether("comment", attrs.commentIds).value ?? []
  return comments.filter(
    (comment) =>
      comment.meta.permissions.includes("can-view-comment") && !comment.attributes.deletedAt,
  )
}

// ----------
// Shared card styles
// ----------

/**
 * Data attrs for cards that are read by common CSS rules (hence should stay in sync between card types).
 */
export function cardWrapperData(facts: { chromeVisible: boolean; isLastCard: boolean }): {
  "data-chrome-visible": true | undefined
  "data-last-card": true | undefined
} {
  return {
    "data-chrome-visible": facts.chromeVisible || undefined,
    "data-last-card": facts.isLastCard || undefined,
  }
}

// Full-bleed chrome geometry shared by ALL card node views,
// which must stay pixel-aligned — same drag-handle position, same
// hover-zone gutter, same comment separator. Single-sourced here so they can't drift.

/** The card wrapper's reveal region: extends the hover zone right (past the card edge,
 *  where the fixed buttons hang) and owns the white above the title as pt-2 padding — a
 *  top margin would collapse out of the useHoverChrome region. Pair with styles.cardWrapper. */
export const CARD_WRAPPER_CLASS = "-mr-9 pr-9 pt-2"

/** The card's bottom separator: an empty trailing div owning the 8px of white below the
 *  card's last element plus the border.
 *  unified-tiptap-card-separator is the hook tests and other extensions select on. */
export const CARD_BOTTOM_SEPARATOR_CLASS =
  "unified-tiptap-card-separator border-card-separator -mx-3 border-b pt-2"

/** The full-bleed comments box below a card: lines up with the card text. */
export const CARD_COMMENTS_BOX_CLASS = "-mx-3 px-3"
